import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import userModel from "../../lib/firebase/userModel";
import {
  DIALOG_NAME,
  useDialogSearchParam,
  useOpenParamDialog,
} from "../../helpers/useDialogSearchParam";
import CustomModal, { ModalDescription, ModalTitle } from "../CustomModal";
import AccountCreationForm from "./AccountCreationForm";
import { useMutation } from "@tanstack/react-query";
import { firebaseAuth, registerFirebaseAuth } from "../../helpers/authentication";
import { message } from "antd";
import { submitFreeGatewaySignup } from "../../lib/api/freeGatewaySignup";
import CreateProfileForm from "./CreateProfileForm";
import useCreateProfileMutation from "../../lib/useCreateProfileMutation";
import NewUserCarousel from "./NewUserCarousel";
import Button from "../Button";
import { Highlight, Spacer } from "../../styles/CommonElements";
import styled from "styled-components";
import { AuthContext } from "../../context/AuthProvider";
import OrDivider from "../OrDivider";
import ThirdPartyAuth from "./ThirdPartyAuth";
import { APP_NAME } from "../../helpers/constants";
import IntakeModal from "../IntakeModal";
import Logo from "../general/Logo";
import { validateInviteCode, registerReferral } from "../../lib/api/inviteAPI";
import { useBreakpoints } from "../../lib/breakpoints";

/** Legacy Firestore fields still expected by older APIs; not shown in the signup form. */
const PROFILE_FIELD_DEFAULTS = {
  propertyType: "other",
  experience: "0-1",
  properties: "2-5",
};

const SignupModal = () => {
  const [searchParams] = useSearchParams();
  const [accountCreated, setAccountCreated] = useState(false);
  const [intakeCompleted, setIntakeCompleted] = useState(false);
  const [intakeData, setIntakeData] = useState(null);
  const [profileCreated, setProfileCreated] = useState(false);
  const [requestCloseConfirmation, setCloseConfirmationRequested] =
    useState(false);
  const [freeGatewayEmail, setFreeGatewayEmail] = useState(null);
  const [freeGatewayTermsAccepted, setFreeGatewayTermsAccepted] =
    useState(false);
  const [freeGatewaySubmitting, setFreeGatewaySubmitting] = useState(false);

  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();

  // Check if this is a trial signup
  const isTrialSignup = searchParams.get("context") === "trial";
  /**
   * PLG 3-Unit Free Gateway signup. Skips Stripe + intake; after profile
   * creation we stamp `selectedPlan: "free"` on the Firestore user doc so the
   * tier-limits middleware caps the account at 3 doors until they upgrade.
   */
  const isFreeSignup =
    searchParams.get("plan") === "free" ||
    searchParams.get("context") === "free";

  // Invite code handling
  const inviteCode = searchParams.get("invite");
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  const { user, userInfo, isUserInfoLoading, isThirdPartyAuthUser } =
    useContext(AuthContext);

  const [isParamSet, removeParam] = useDialogSearchParam(DIALOG_NAME.signup);
  const openLogin = useOpenParamDialog(DIALOG_NAME.login);

  /**
   * `/?signup=` (empty param from useOpenParamDialog) opens the same IntakeModal
   * flow as marketing CTAs - not the legacy email/password sheet first.
   * Trial: intake → account → profile. Free/paid self-serve: IntakeModal only.
   */
  const standaloneIntakeOpen = useMemo(() => {
    if (!isParamSet || profileCreated || requestCloseConfirmation) return false;
    if (isThirdPartyAuthUser) return false;
    if (isFreeSignup) return true;
    if (isTrialSignup && !intakeCompleted) return true;
    if (!isTrialSignup && !isFreeSignup) return true;
    return false;
  }, [
    isParamSet,
    profileCreated,
    requestCloseConfirmation,
    isThirdPartyAuthUser,
    isFreeSignup,
    isTrialSignup,
    intakeCompleted,
  ]);

  const legacySignupModalOpen = useMemo(
    () => isParamSet && !standaloneIntakeOpen,
    [isParamSet, standaloneIntakeOpen]
  );

  const intakeSelectedPlan = useMemo(() => {
    // Strategic pivot: Everyone is individual (pay-as-you-go) now
    return "individual";
  }, []);

  const handleCloseStandaloneIntake = useCallback(() => {
    setIntakeCompleted(false);
    setIntakeData(null);
    removeParam();
  }, [removeParam]);

  const {
    mutate: createAccount,
    isPending: accountCreationPending,
    error: createAccountErrors,
  } = useMutation({
    mutationFn: async ({ email, password }) => {
      const result = await registerFirebaseAuth({
        email,
        password,
      });

      if (!result) {
        throw new Error("An account with this email already exists");
      }
    },
    onSuccess: async () => {
      setAccountCreated(true);

      // Register referral if invite code is present
      if (inviteCode && firebaseAuth.currentUser) {
        try {
          const userEmail = firebaseAuth.currentUser.email;
          await registerReferral(inviteCode, userEmail);
          console.log("Referral registered successfully");
        } catch (error) {
          console.error("Failed to register referral:", error);
          // Don't block signup flow if referral registration fails
        }
      }
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const { mutate: createProfile, isPending: profileCreationPending } =
    useCreateProfileMutation();

  const onClose = useCallback(
    (e, force = false) => {
      const requiresCloseConfirmation =
        (accountCreated && !profileCreated) ||
        (isThirdPartyAuthUser && !profileCreated);

      // The only way to close from here is to call onClose with force via the handleExitHalfway function (see below)
      if (requiresCloseConfirmation && !force) {
        setCloseConfirmationRequested(true);
        return;
      }

      setAccountCreated(false);
      setIntakeCompleted(false);
      setIntakeData(null);
      setProfileCreated(false);
      setFreeGatewayEmail(null);
      setFreeGatewayTermsAccepted(false);
      setFreeGatewaySubmitting(false);
      removeParam();
    },
    [accountCreated, isThirdPartyAuthUser, profileCreated, removeParam]
  );

  // Trying to account for the user opening the modal at a weird stage in the process
  useEffect(() => {
    if (user && !isUserInfoLoading) {
      if (userInfo && isParamSet && !profileCreated) {
        onClose();
      }
      // The user exists in Firebase Auth but not in our data yet, so we skip to profile creation
      // Third party auth users still should be shown a screen to "approve" TOS first.
      else if (!userInfo && !isThirdPartyAuthUser) {
        setAccountCreated(true);
        // Trial signup needs the intake step; free signup and regular flows skip it.
        if (isTrialSignup && !intakeData) {
          setIntakeCompleted(false);
        } else {
          setIntakeCompleted(true);
        }
      }
    }
  }, [
    isParamSet,
    isThirdPartyAuthUser,
    isUserInfoLoading,
    onClose,
    user,
    userInfo,
    isTrialSignup,
    isFreeSignup,
    intakeData,
    profileCreated,
  ]);

  // Validate invite UID on component mount (but don't show duplicate messages)
  useEffect(() => {
    if (inviteCode) {
      setInviteLoading(true);
      validateInviteCode(inviteCode)
        .then((data) => {
          setInviteData(data);
          // Don't show success message here - it's already shown on the signup page
          if (!data.success || !data.valid) {
            message.warning("Invalid invite link");
          }
        })
        .catch((error) => {
          console.error("Error validating invite:", error);
          message.warning("Could not validate invite link");
          setInviteData({ valid: false, expired: false });
        })
        .finally(() => {
          setInviteLoading(false);
        });
    }
  }, [inviteCode]);

  const onAccountCreationSubmit = (data) => {
    if (isThirdPartyAuthUser) {
      setAccountCreated(true);
      return;
    }

    if (isFreeSignup) {
      setFreeGatewayEmail(data.email);
      setFreeGatewayTermsAccepted(data.agreedToTerms === true);
      setAccountCreated(true);
      setIntakeCompleted(true);
      return;
    }

    createAccount(data);
  };

  const onIntakeSubmit = async (data) => {
    setIntakeData(data);
    setIntakeCompleted(true);

    // For trial users, we can also set up their trial subscription here if needed
    // This will be handled in the profile creation step
  };

  const onProfileCreationSubmit = (data) => {
    if (isFreeSignup && !isThirdPartyAuthUser) {
      setFreeGatewaySubmitting(true);
      submitFreeGatewaySignup({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        properties: data.properties ?? PROFILE_FIELD_DEFAULTS.properties,
        propertyType: data.propertyType ?? PROFILE_FIELD_DEFAULTS.propertyType,
        experience: data.experience ?? PROFILE_FIELD_DEFAULTS.experience,
        pricingTierGroup: "market_2026",
        inviteCode: inviteCode || undefined,
        agreeToTerms: freeGatewayTermsAccepted === true,
      })
        .then(() => {
          message.success(
            "Check your email for your temporary password, then sign in."
          );
          setProfileCreated(true);
        })
        .catch((err) => {
          message.error(err?.message || "Could not complete signup.");
        })
        .finally(() => setFreeGatewaySubmitting(false));
      return;
    }

    const profileData =
      isTrialSignup && intakeData
        ? {
            ...PROFILE_FIELD_DEFAULTS,
            ...data,
            ...intakeData,
            isProvider: true,
            isTrial: true,
          }
        : isFreeSignup && isThirdPartyAuthUser
          ? {
              ...PROFILE_FIELD_DEFAULTS,
              ...data,
              selectedPlan: "free",
              pricingTierGroup: "market_2026",
              subscriptionStatus: "free",
            }
          : { ...PROFILE_FIELD_DEFAULTS, ...data };

    createProfile(profileData, {
      onSuccess: async () => {
        setProfileCreated(true);
        if (isFreeSignup && user?.uid && isThirdPartyAuthUser) {
          try {
            await userModel.update({
              id: user.uid,
              data: {
                selectedPlan: "free",
                pricingTierGroup: "market_2026",
                subscriptionStatus: "free",
              },
            });
          } catch (err) {
            console.error("[SignupModal] Free plan persist failed:", err);
          }
          navigate("/chat");
        }
      },
    });
  };

  const handleExitHalfway = () => {
    firebaseAuth.signOut();
    onClose({}, true);
    setCloseConfirmationRequested(false);
  };

  return (
    <>
      <IntakeModal
        isOpen={standaloneIntakeOpen}
        onClose={handleCloseStandaloneIntake}
        selectedPlan={intakeSelectedPlan}
        onSubmit={isTrialSignup ? onIntakeSubmit : undefined}
        marketingSignup={!isTrialSignup}
        pricingProduct="procurement"
      />
    <CustomModal
      isOpen={legacySignupModalOpen}
      onClose={onClose}
      showCloseIcon={!requestCloseConfirmation}
      isCentered={true}
      width={500}
    >
      {!requestCloseConfirmation && (
        <>
          {/* Account creation (email/password or Google) */}
          {!accountCreated && (
            <>
              <Logo />
              <Spacer $marginTop="0.25rem" />
              <ModalTitle>{`Sign Up for ${APP_NAME}`}</ModalTitle>
              {!isThirdPartyAuthUser && (
                <ModalDescription>
                  Create your account. Clumse focuses on vendor bids, repairs, and
                  getting you vetted quotes without the runaround. Deeper rent ledgers and in-app books ship on
                  higher tiers when you need them.
                </ModalDescription>
              )}
              <Spacer $marginTop="1.5rem" />
              <AccountCreationForm
                isThirdPartyUser={isThirdPartyAuthUser}
                skipPassword={isFreeSignup && !isThirdPartyAuthUser}
                onSubmit={onAccountCreationSubmit}
                isSubmitting={accountCreationPending}
                inviteCode={inviteCode}
                inviteData={inviteData}
                inviteLoading={inviteLoading}
              />
              {createAccountErrors && (
                <MutationError>{createAccountErrors.message}</MutationError>
              )}
              {!isThirdPartyAuthUser && (
                <>
                  <Spacer $marginTop="1rem" />
                  <OrDivider />
                  <Spacer $marginTop="1rem" />
                  <ThirdPartyAuth cta="Sign up" />
                </>
              )}
              <LoginCta>
                <Highlight
                  onClick={() => {
                    if (isMobile) {
                      removeParam();
                      navigate("/login");
                      return;
                    }
                    openLogin();
                  }}
                >
                  Already have an account? Log In
                </Highlight>
              </LoginCta>
              <Spacer $marginTop="0.25rem" />
            </>
          )}
          {/* Profile Creation */}
          {accountCreated && intakeCompleted && !profileCreated && (
            <>
              <ModalTitle>
                {isTrialSignup ? "Almost done!" : "Tell us about yourself"}
              </ModalTitle>
              <Subtitle>
                {isTrialSignup
                  ? "Just a few more details to complete your setup"
                  : "This helps us optimize your experience"}
              </Subtitle>
              <CreateProfileForm
                onSubmit={onProfileCreationSubmit}
                isSubmitting={profileCreationPending || freeGatewaySubmitting}
                defaultEmail={freeGatewayEmail || undefined}
              />
            </>
          )}
          {/* New User Info */}
          {profileCreated &&
            (isFreeSignup && !isThirdPartyAuthUser ? (
              <>
                <ModalTitle>Check your email</ModalTitle>
                <ModalDescription style={{ marginTop: "0.5rem" }}>
                  We sent your temporary password and sign-in link to your inbox.
                  You can close this window and log in from your email.
                </ModalDescription>
              </>
            ) : (
              <NewUserCarousel />
            ))}
        </>
      )}
      {requestCloseConfirmation && (
        <>
          <ModalTitle>Quit Profile Creation?</ModalTitle>
          <ModalDescription style={{ margin: "1rem 0px 1.5rem 0px" }}>
            You've created your account, but your profile is incomplete. If you
            leave now, your changes won't be saved.
          </ModalDescription>
          <Button
            variant="primary"
            onClick={() => {
              setCloseConfirmationRequested(false);
            }}
            style={{
              background: "var(--gradient-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              boxShadow: "0 4px 18px rgba(44, 95, 45, 0.15)",
              fontFamily: "var(--font-secondary)",
              fontWeight: "700",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--gradient-primary-dark)";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 32px rgba(44, 95, 45, 0.25)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--gradient-primary)";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 18px rgba(44, 95, 45, 0.15)";
            }}
          >
            Complete Profile
          </Button>
          <Spacer $marginTop="0.75rem" />
          <Button
            style={{
              color: "var(--color-text)",
              background: "var(--color-bg-secondary)",
              border: "2px solid var(--color-border-light)",
              borderRadius: "12px",
              fontFamily: "var(--font-secondary)",
              fontWeight: "600",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            variant="ghost"
            onClick={handleExitHalfway}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--color-bg-tertiary)";
              e.target.style.borderColor = "var(--color-primary)";
              e.target.style.color = "var(--color-primary)";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--color-bg-secondary)";
              e.target.style.borderColor = "var(--color-border-light)";
              e.target.style.color = "var(--color-text)";
              e.target.style.transform = "translateY(0)";
            }}
          >
            Close
          </Button>
        </>
      )}
      <Spacer $marginTop="1rem" />
    </CustomModal>
    </>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
  height: 60px;
  width: auto;
  margin: auto;
`;

const Subtitle = styled.p`
  color: var(--color-text-light);
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.75rem;
  margin: 0;
`;

const LoginCta = styled.div`
  color: var(--color-text);
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;
  margin-top: 16px;

  span {
    cursor: pointer;
  }
`;

const MutationError = styled.div`
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  color: var(--color-error);
  margin-top: 12px;
`;

export default SignupModal;
